import WolfButton from '@components/ui/WolfButton';
import { graphQlClient } from '@config/graphqlClient';
import DiscrepanciesGraphQL from '@graphql/discrepancy.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { Discrepancy, updateDiscrepancyRequest } from '@models/discrepancy.model';
import {
    ExpandLessOutlined,
    LightbulbOutlined
} from '@mui/icons-material';
import {
    Box, IconButton,
    Paper, Typography
} from '@mui/material';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface ProcessDiscrepancyPanelProps {
  discrepancies: Discrepancy[];
  onDiscrepancyUpdate?: () => void;
  initiallyOpen?: boolean;
}

const ProcessDiscrepancyPanel: React.FC<ProcessDiscrepancyPanelProps> = ({
  discrepancies,
  onDiscrepancyUpdate,
  initiallyOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [loadingDiscrepancyId, setLoadingDiscrepancyId] = useState<string | null>(null);

  const locale = useTranslation('discrepancies');
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');
  const dispatch = useDispatch();


  // Highlight animation on load
  useEffect(() => {
    if (discrepancies.length > 0) {
      setIsHighlighted(true);
      const timer = setTimeout(() => setIsHighlighted(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [discrepancies]);

  // Open panel when initiallyOpen prop is true
  useEffect(() => {
    if (initiallyOpen) {
      setIsOpen(true);
    }
  }, [initiallyOpen]);


  const handleOptionSelected = async (
    selected: string,
    discrepancy: Discrepancy,
    ignoreType?: string,
  ) => {
    setLoadingDiscrepancyId(discrepancy.discrepancyId || null);

    // Calculate ignoreUntil date based on ignoreType
    let ignoreUntil: string | null = null;
    if (ignoreType === 'day') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      ignoreUntil = tomorrow.toISOString();
    } else if (ignoreType === 'month') {
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      ignoreUntil = nextMonth.toISOString();
    }
    // If ignoreType is 'permanent', ignoreUntil remains null

    const variables = {
      discrepancyId: discrepancy.discrepancyId,
      action: selected,
      ignoreUntil: ignoreUntil,
    };

    try {
      const data: updateDiscrepancyRequest = await graphQlClient.request(
        DiscrepanciesGraphQL.mutations.updateDiscrepancy,
        { discrepancy: variables },
      );

      if (data?.updateDiscrepancy) {
        dispatch(
          appendActionMessage({
            message: localeActionMessages['discrepancyUpdatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );

        // Call callback to refresh process data
        if (onDiscrepancyUpdate) {
          onDiscrepancyUpdate();
        }
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoadingDiscrepancyId(null);
    }
  };

  if (!discrepancies || discrepancies.length === 0) {
    return null;
  }

  return (
    <Box className="relative inline-block">
      {/* Lightbulb header */}
      <Box
        className={`cursor-pointer transition-all duration-500 ${
          isHighlighted ? 'animate-pulse scale-110' : ''
        }`}
        onClick={() => setIsOpen(!isOpen)}>
        <Box className={`bg-yellow-100 border border-yellow-300 rounded-full shadow-lg flex items-center justify-center ${
          isHighlighted ? 'ring-2 ring-yellow-400 ring-opacity-75' : ''
        }`}
        sx={{ width: '30px', height: '30px' }}>
          <LightbulbOutlined
            className={`text-yellow-600 transition-all duration-300 ${
              isHighlighted ? 'drop-shadow-lg' : ''
            }`}
            fontSize="medium"
          />
        </Box>
      </Box>

      {/* Panel content */}
      {isOpen && (
        <Paper
          className="absolute top-8 right-0 z-50 w-80 shadow-lg"
          elevation={3}>
          <Box className="p-3">
            <Box className="flex items-center justify-between mb-2">
              <Typography variant="h6" className="font-semibold">
                {locale['possibleImprovements']}
              </Typography>
              <IconButton size="small" onClick={() => setIsOpen(false)}>
                <ExpandLessOutlined />
              </IconButton>
            </Box>

        <Box>
          <Box className="space-y-3">
            {discrepancies.map((discrepancy) => (
              <Box
                key={discrepancy.discrepancyId}
                className="border rounded-lg p-3 bg-gray-50">
                <Typography variant="subtitle2" className="font-semibold mb-2">
                  {discrepancy.discrepancyData?.discrepancyDetailsTitle}
                </Typography>

                {discrepancy.discrepancyData?.discrepancyDetailsContent?.map(
                  (detail: string, detailIndex: number) => (
                    <Typography key={detailIndex} variant="body2" className="mb-1 text-gray-700">
                      • {detail}
                    </Typography>
                  ),
                )}

                <Box className="flex flex-col gap-2 mt-3">
                  <Box className="flex gap-2">
                    {(() => {
                      const optionCode = discrepancy?.discrepancyData?.optionWolf || discrepancy?.discrepancyData?.optionP || '';
                      const discrepancyType = discrepancy?.discrepancyData?.discrepancyType;
                      const typeData = locale?.types?.[discrepancyType as string];
                      // Use optionWolfProcessPage for process page, optionWolf for discrepancies list
                      const label = typeData?.optionWolfProcessPage?.label || typeData?.optionWolf?.label || locale['okDoneIt'] || '';
                      const message = typeData?.optionWolfProcessPage?.message || typeData?.optionWolf?.message || discrepancy?.discrepancyData?.optionWolfMessage || discrepancy?.discrepancyData?.optionPMessage || '';

                      return (
                        <WolfButton
                          size="small"
                          variant="outlined"
                          color="primary"
                          className="flex-1"
                          title={message}
                          disabled={loadingDiscrepancyId === discrepancy.discrepancyId}
                          onClick={() => handleOptionSelected(optionCode, discrepancy)}>
                          {label}
                        </WolfButton>
                      );
                    })()}
                  </Box>

                  <Box className="flex flex-col gap-2">
                    <WolfButton
                      size="small"
                      variant="outlined"
                      color="secondary"
                      className="w-full"
                      disabled={loadingDiscrepancyId === discrepancy.discrepancyId}
                      onClick={() => handleOptionSelected(
                        'IGNORE',
                        discrepancy,
                        'day'
                      )}>
                      {locale['remindInDay']}
                    </WolfButton>

                    <WolfButton
                      size="small"
                      variant="outlined"
                      color="secondary"
                      className="w-full"
                      disabled={loadingDiscrepancyId === discrepancy.discrepancyId}
                      onClick={() => handleOptionSelected(
                        'IGNORE',
                        discrepancy,
                        'month'
                      )}>
                      {locale['remindInMonth']}
                    </WolfButton>

                    <WolfButton
                      size="small"
                      variant="outlined"
                      color="secondary"
                      className="w-full"
                      disabled={loadingDiscrepancyId === discrepancy.discrepancyId}
                      onClick={() => handleOptionSelected(
                        'IGNORE',
                        discrepancy,
                        'permanent'
                      )}>
                      {locale['discardPermanently']}
                    </WolfButton>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ProcessDiscrepancyPanel;